import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  MessageCircle, Mail, ArrowLeft, ArrowRight, ChevronDown, ChevronRight,
  HelpCircle, Ticket, CreditCard, Home, Clock, QrCode, XCircle, MapPin,
  FileText, Users, Phone, ExternalLink, CheckCircle, AlertTriangle,
  Sparkles, Calendar, Shield, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

// Configurações de contato (podem vir do backend futuramente)
const WHATSAPP_NUMBER = "5569999999999";
const WHATSAPP_MESSAGE = "Olá! Preciso de ajuda com o Encontro de Casais Kairós 2027.";
const EMAIL_CONTATO = "contato@kairos.com.br";
const HORARIO_ATENDIMENTO = "Segunda a Sexta, 8h às 18h";

const ContatoPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  // FAQ items
  const faqItems = [
    {
      icon: Calendar,
      question: "Quando será o Kairós 2027 e como me pré-inscrevo?",
      answer: "A próxima edição acontecerá de 12 a 15 de novembro de 2027, no Hotel Fazenda Minuano (Ji-Paraná/RO). As pré-inscrições estão abertas e são gratuitas: deixe os dados do casal e receba em primeira mão a abertura do primeiro lote e os valores.",
      link: "/pre-inscricao",
      linkText: "Fazer pré-inscrição",
      color: "primary"
    },
    {
      icon: Mail,
      question: "Não recebi o e-mail de confirmação (edição 2026)",
      answer: "Verifique sua caixa de spam ou lixo eletrônico. O e-mail é enviado automaticamente após a aprovação do pagamento. Se já foi aprovado e não recebeu, acesse sua inscrição para ver o status.",
      link: "/minha-inscricao",
      linkText: "Ver minha inscrição",
      color: "primary"
    },
    {
      icon: CreditCard,
      question: "Meu pagamento está pendente (PIX)",
      answer: "Pagamentos via PIX podem levar alguns minutos para serem confirmados. Se já fez o pagamento, aguarde até 30 minutos. Caso persista, entre em contato pelo WhatsApp com o comprovante.",
      link: "/minha-inscricao",
      linkText: "Verificar status",
      color: "warning"
    },
    {
      icon: Users,
      question: "Quero alterar meus dados",
      answer: "Para alterações nos dados cadastrados (nome, telefone, etc.), entre em contato pelo WhatsApp informando o e-mail da inscrição e os dados que deseja alterar.",
      link: null,
      linkText: null,
      color: "secondary"
    },
    {
      icon: Home,
      question: "Como funciona a hospedagem/suíte?",
      answer: "Em 2026 cada casal teve suíte exclusiva no Hotel Fazenda Minuano. Os detalhes de hospedagem da edição 2027 serão confirmados junto com a abertura do primeiro lote.",
      link: "/pre-inscricao",
      linkText: "Quero ser avisado",
      color: "info"
    },
    {
      icon: Clock,
      question: "Check-in e check-out: quais horários?",
      answer: "Os horários de check-in e check-out da edição 2027 serão divulgados junto com a abertura do primeiro lote.",
      link: null,
      linkText: null,
      color: "success"
    },
    {
      icon: QrCode,
      question: "Como usar o QR Code no evento?",
      answer: "O QR Code é gerado automaticamente após a aprovação do pagamento. Apresente-o na recepção do hotel no momento do check-in. Você pode baixá-lo na página 'Minha Inscrição'.",
      link: "/minha-inscricao",
      linkText: "Acessar meu QR Code",
      color: "primary"
    },
    {
      icon: XCircle,
      question: "Posso cancelar minha inscrição?",
      answer: "O cancelamento está sujeito às condições descritas nos Termos de Participação aceitos no momento da inscrição. Para solicitar cancelamento, entre em contato pelo WhatsApp.",
      link: null,
      linkText: null,
      color: "danger"
    },
    {
      icon: MapPin,
      question: "Onde será o Kairós 2027?",
      answer: "No Hotel Fazenda Minuano, na estrada entre Ji-Paraná e Presidente Médici, em Rondônia — o mesmo local da edição 2026.",
      link: "/pre-inscricao",
      linkText: "Fazer pré-inscrição",
      color: "secondary"
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      primary: {
        bg: "bg-[var(--ds-primary)]/10",
        border: "border-[var(--ds-primary)]/30",
        text: "text-[var(--ds-primary)]",
        icon: "text-[var(--ds-primary)]"
      },
      secondary: {
        bg: "bg-[var(--ds-secondary)]/10",
        border: "border-[var(--ds-secondary)]/30",
        text: "text-[var(--ds-secondary)]",
        icon: "text-[var(--ds-secondary)]"
      },
      success: {
        bg: "bg-[var(--ds-success)]/10",
        border: "border-[var(--ds-success)]/30",
        text: "text-[var(--ds-success)]",
        icon: "text-[var(--ds-success)]"
      },
      warning: {
        bg: "bg-[var(--ds-warning)]/10",
        border: "border-[var(--ds-warning)]/30",
        text: "text-[var(--ds-warning)]",
        icon: "text-[var(--ds-warning)]"
      },
      danger: {
        bg: "bg-[var(--ds-danger)]/10",
        border: "border-[var(--ds-danger)]/30",
        text: "text-[var(--ds-danger)]",
        icon: "text-[var(--ds-danger)]"
      },
      info: {
        bg: "bg-[var(--ds-info)]/10",
        border: "border-[var(--ds-info)]/30",
        text: "text-[var(--ds-info)]",
        icon: "text-[var(--ds-info)]"
      }
    };
    return colors[color] || colors.primary;
  };

  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

  return (
    <>
      <Helmet>
        <title>Contato e Suporte | Encontro de Casais Kairós 2027</title>
        <meta name="description" content="Precisa de ajuda com o Encontro de Casais Kairós 2027? Encontre respostas rápidas ou fale conosco pelo WhatsApp." />
      </Helmet>

      <div className="min-h-screen bg-[var(--ds-background)]" data-testid="contato-page">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[var(--ds-background)]/90 backdrop-blur-md border-b border-[var(--ds-border)]">
          <div className="container mx-auto px-4 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <ArrowLeft className="w-5 h-5 text-[var(--ds-text-secondary)]" />
              <img src="/logo-kairos.png" alt="Kairós" className="w-10 h-10 rounded-full object-cover" />
              <span className="font-display text-xl text-[var(--ds-text-primary)]">Kairós</span>
            </Link>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
              <Button className="bg-gray-800 hover:bg-green-600 text-white rounded-full px-6 h-10">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </a>
          </div>
        </nav>

        {/* ══════════════════════════════════════════════════════════════════════
            1️⃣ HERO
        ══════════════════════════════════════════════════════════════════════ */}
        <section className="pt-32 pb-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--ds-background)] via-[var(--ds-surface)] to-[var(--ds-background)]" />
          <div className="absolute top-20 right-10 w-64 h-64 bg-gray-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[var(--ds-primary)]/10 rounded-full blur-3xl" />

          <div className="container mx-auto px-4 relative z-10 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-500/10 border border-gray-500/30 mb-6">
              <MessageCircle className="w-4 h-4 text-green-500" />
              <span className="text-green-500 text-sm font-medium">SUPORTE</span>
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--ds-text-primary)] mb-6">
              Fale com a <span className="text-green-500">organização</span>
            </h1>

            <p className="text-[var(--ds-text-secondary)] text-lg md:text-xl mb-10 max-w-2xl mx-auto">
              Se precisar de ajuda, estamos aqui. Mas antes, veja as 
              <span className="text-[var(--ds-text-primary)] font-medium"> respostas rápidas</span> abaixo.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                <Button 
                  className="h-16 px-10 rounded-full bg-gray-800 hover:bg-green-600 text-white font-semibold text-lg shadow-[0_0_30px_rgba(34,197,94,0.4)] hover:shadow-[0_0_50px_rgba(34,197,94,0.6)] transition-all transform hover:scale-105"
                  data-testid="whatsapp-btn"
                >
                  <MessageCircle className="w-6 h-6 mr-3" />
                  Chamar no WhatsApp
                </Button>
              </a>
              
              <Link to="/minha-inscricao">
                <Button 
                  variant="outline"
                  className="h-14 px-8 rounded-full bg-transparent border-[var(--ds-border)] text-[var(--ds-text-primary)] hover:bg-[var(--ds-surface-hover)]"
                >
                  <Ticket className="w-5 h-5 mr-2" />
                  Acessar Minha Inscrição
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════════
            2️⃣ RESPOSTAS RÁPIDAS (FAQ)
        ══════════════════════════════════════════════════════════════════════ */}
        <section className="py-16 bg-[var(--ds-surface)]">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl md:text-4xl text-[var(--ds-text-primary)] mb-4">
                Respostas <span className="text-[var(--ds-primary)]">rápidas</span>
              </h2>
              <p className="text-[var(--ds-text-secondary)] max-w-2xl mx-auto">
                Encontre a resposta que precisa sem esperar. Clique em uma pergunta para ver a resposta.
              </p>
            </div>

            <div className="max-w-3xl mx-auto space-y-3">
              {faqItems.map((item, index) => {
                const colors = getColorClasses(item.color);
                const isOpen = openFaq === index;
                
                return (
                  <Card 
                    key={index}
                    className={`bg-[var(--ds-background)] border ${isOpen ? colors.border : 'border-[var(--ds-border)]'} transition-all cursor-pointer`}
                    onClick={() => toggleFaq(index)}
                  >
                    <CardContent className="p-0">
                      {/* Question */}
                      <div className="flex items-center gap-4 p-5">
                        <div className={`w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                          <item.icon className={`w-5 h-5 ${colors.icon}`} />
                        </div>
                        <span className="flex-1 text-[var(--ds-text-primary)] font-medium text-left">
                          {item.question}
                        </span>
                        <ChevronDown className={`w-5 h-5 text-[var(--ds-text-secondary)] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </div>
                      
                      {/* Answer */}
                      {isOpen && (
                        <div className="px-5 pb-5 pt-0">
                          <div className={`p-4 rounded-xl ${colors.bg} border ${colors.border}`}>
                            <p className="text-[var(--ds-text-secondary)] text-sm leading-relaxed mb-3">
                              {item.answer}
                            </p>
                            {item.link && (
                              <Link 
                                to={item.link}
                                onClick={(e) => e.stopPropagation()}
                                className={`inline-flex items-center gap-2 text-sm font-medium ${colors.text} hover:underline`}
                              >
                                {item.linkText}
                                <ChevronRight className="w-4 h-4" />
                              </Link>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════════
            3️⃣ CANAIS DE ATENDIMENTO
        ══════════════════════════════════════════════════════════════════════ */}
        <section className="py-16 bg-[var(--ds-background)]">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl md:text-4xl text-[var(--ds-text-primary)] mb-4">
                Canais de <span className="text-[var(--ds-secondary)]">atendimento</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {/* WhatsApp */}
              <Card className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-2 border-gray-500/30 hover:border-gray-500/50 transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gray-500/20 flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-7 h-7 text-green-500" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-[var(--ds-text-primary)] font-semibold text-lg">WhatsApp</h3>
                        <span className="px-2 py-0.5 rounded-full bg-gray-500/20 text-green-500 text-xs font-medium">Principal</span>
                      </div>
                      <p className="text-[var(--ds-text-secondary)] text-sm mb-3">
                        Canal mais rápido para atendimento
                      </p>
                      <div className="flex items-center gap-2 text-[var(--ds-text-muted)] text-sm mb-4">
                        <Clock className="w-4 h-4" />
                        <span>{HORARIO_ATENDIMENTO}</span>
                      </div>
                      <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                        <Button className="w-full bg-gray-800 hover:bg-green-600 text-white rounded-full">
                          Iniciar conversa
                          <ExternalLink className="w-4 h-4 ml-2" />
                        </Button>
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* E-mail */}
              <Card className="bg-gradient-to-br from-[var(--ds-secondary)]/10 to-[var(--ds-secondary)]/5 border-2 border-[var(--ds-secondary)]/30 hover:border-[var(--ds-secondary)]/50 transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-[var(--ds-secondary)]/20 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-7 h-7 text-[var(--ds-secondary)]" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-[var(--ds-text-primary)] font-semibold text-lg">E-mail</h3>
                        <span className="px-2 py-0.5 rounded-full bg-[var(--ds-secondary)]/20 text-[var(--ds-secondary)] text-xs font-medium">Secundário</span>
                      </div>
                      <p className="text-[var(--ds-text-secondary)] text-sm mb-3">
                        Para assuntos formais ou documentos
                      </p>
                      <div className="flex items-center gap-2 text-[var(--ds-text-muted)] text-sm mb-4">
                        <Clock className="w-4 h-4" />
                        <span>Resposta em até 24h úteis</span>
                      </div>
                      <a href={`mailto:${EMAIL_CONTATO}`}>
                        <Button variant="outline" className="w-full border-[var(--ds-secondary)]/30 text-[var(--ds-secondary)] hover:bg-[var(--ds-secondary)]/10 rounded-full">
                          {EMAIL_CONTATO}
                        </Button>
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════════
            4️⃣ PARA QUEM JÁ SE INSCREVEU
        ══════════════════════════════════════════════════════════════════════ */}
        <section className="py-16 bg-[var(--ds-surface)]">
          <div className="container mx-auto px-4">
            <Card className="max-w-2xl mx-auto bg-gradient-to-br from-[var(--ds-primary)]/10 to-[var(--ds-primary)]/5 border-2 border-[var(--ds-primary)]/30">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-2xl bg-[var(--ds-primary)]/20 flex items-center justify-center mx-auto mb-6">
                  <Ticket className="w-8 h-8 text-[var(--ds-primary)]" />
                </div>
                
                <h3 className="font-display text-2xl text-[var(--ds-text-primary)] mb-3">
                  Já está inscrito?
                </h3>
                <p className="text-[var(--ds-text-secondary)] mb-6">
                  Acesse sua inscrição para ver status, baixar QR Code e mais.
                </p>
                
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link to="/minha-inscricao">
                    <Button className="bg-[var(--ds-primary)] hover:bg-[var(--ds-primary-dark)] text-white rounded-full px-8">
                      <Ticket className="w-5 h-5 mr-2" />
                      Ver minha inscrição
                    </Button>
                  </Link>
                  <Link to="/minha-inscricao">
                    <Button variant="outline" className="border-[var(--ds-primary)]/30 text-[var(--ds-primary)] hover:bg-[var(--ds-primary)]/10 rounded-full px-8">
                      <QrCode className="w-5 h-5 mr-2" />
                      Baixar QR Code
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════════
            5️⃣ TRANSPARÊNCIA
        ══════════════════════════════════════════════════════════════════════ */}
        <section className="py-12 bg-[var(--ds-background)]">
          <div className="container mx-auto px-4">
            <Card className="max-w-2xl mx-auto bg-[var(--ds-surface)] border border-[var(--ds-border)]">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[var(--ds-warning)]/10 flex items-center justify-center flex-shrink-0">
                    <Info className="w-5 h-5 text-[var(--ds-warning)]" />
                  </div>
                  <div>
                    <h3 className="text-[var(--ds-text-primary)] font-semibold mb-2">Transparência</h3>
                    <ul className="space-y-2 text-[var(--ds-text-secondary)] text-sm">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-[var(--ds-success)] flex-shrink-0 mt-0.5" />
                        <span>Durante alta demanda, priorizamos inscritos com <span className="text-[var(--ds-text-primary)]">pagamento aprovado</span>.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-[var(--ds-warning)] flex-shrink-0 mt-0.5" />
                        <span>Para dúvidas sobre cancelamento, consulte os <span className="text-[var(--ds-text-primary)]">Termos de Participação</span> aceitos na inscrição.</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════════════════════════
            LINKS RÁPIDOS
        ══════════════════════════════════════════════════════════════════════ */}
        <section className="py-12 bg-[var(--ds-surface)]">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <h3 className="text-[var(--ds-text-primary)] font-semibold text-lg">Links úteis</h3>
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-4 max-w-3xl mx-auto">
              <Link to="/pre-inscricao" className="flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--ds-background)] border border-[var(--ds-border)] text-[var(--ds-text-secondary)] hover:text-[var(--ds-text-primary)] hover:border-[var(--ds-primary)]/30 transition-colors">
                <Calendar className="w-4 h-4" />
                Pré-inscrição 2027
              </Link>
              <Link to="/politica-de-privacidade" className="flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--ds-background)] border border-[var(--ds-border)] text-[var(--ds-text-secondary)] hover:text-[var(--ds-text-primary)] hover:border-[var(--ds-primary)]/30 transition-colors">
                <Sparkles className="w-4 h-4" />
                Política de Privacidade
              </Link>
              <Link to="/" className="flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--ds-background)] border border-[var(--ds-border)] text-[var(--ds-text-secondary)] hover:text-[var(--ds-text-primary)] hover:border-[var(--ds-primary)]/30 transition-colors">
                <Home className="w-4 h-4" />
                Home
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 bg-[var(--ds-background)] border-t border-[var(--ds-border)]">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-3">
                <img src="/logo-kairos.png" alt="Kairós" className="w-10 h-10 rounded-full object-cover" />
                <div>
                  <p className="text-[var(--ds-text-primary)] font-medium">Encontro de Casais Kairós</p>
                  <p className="text-[var(--ds-text-muted)] text-sm">Seminário Psicoteológico</p>
                </div>
              </div>

              <div className="flex items-center gap-6 text-sm">
                <Link to="/" className="text-[var(--ds-text-secondary)] hover:text-[var(--ds-text-primary)] transition-colors">Home</Link>
                <Link to="/pre-inscricao" className="text-[var(--ds-text-secondary)] hover:text-[var(--ds-text-primary)] transition-colors">Pré-inscrição</Link>
                <Link to="/minha-inscricao" className="text-[var(--ds-text-secondary)] hover:text-[var(--ds-text-primary)] transition-colors">Minha Inscrição</Link>
              </div>

              <p className="text-[var(--ds-text-muted)] text-sm">
                © {new Date().getFullYear()} Kairós. Todos os direitos reservados.
              </p>
            </div>
            
            {/* Créditos Groover Digital */}
            <div className="mt-8 pt-6 border-t border-[var(--ds-border)] text-center">
              <p className="text-[var(--ds-text-muted)] text-xs">
                Desenvolvido por{' '}
                <a 
                  href="https://grooverdigital.com.br" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[var(--ds-primary)] hover:text-[var(--ds-primary-light)] transition-colors font-medium"
                >
                  Groover Digital e Tecnologia
                </a>
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ContatoPage;
